/**
 * Staff-ui firm-tab form (create + edit) for the two staff-authorable firm-tab
 * kinds: client testimonials and diversity.
 *
 * Aligned with the canonical account-ui `FirmTabForm`
 * (`services/account-ui/src/pages/Profiles/FirmTabForm/FirmTabForm.tsx`) via the
 * shared staff-ui primitives in `@/components/Profiles` (StatusPill, SaveActions,
 * FormValidationSummary, useFormValidation, Repeater, ImageUploadField, LanguageTabBar,
 * useLanguageVersions, RichTextField). Status is driven by which SaveActions
 * button is pressed — there is no editable status field — and Publish is gated
 * behind `profiles.publish`. Staff-only additions (the CompanyPicker in create
 * mode; resolving the firm NAME from the address-book in edit mode) are retained
 * because staff author across any firm, unlike the account portal which derives
 * the firm from the session.
 */
import { Button, Input } from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { companies } from "@/api/addressBook.service";
import { extractZodIssues } from "@/api/profiles";
import {
  type DiversityImage,
  firmTabsService,
  type FirmTabKind,
  type FirmTabTestimonialEntryInput,
} from "@/api/profiles/firmTabsService";
import type { ContentStatus, FirmTab, RegionAssignment } from "@/api/profiles/types";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PageHeader } from "@/components/PageHeader";
import {
  FormValidationSummary,
  ImageUploadField,
  LanguageTabBar,
  type LanguageVersion,
  labelForLanguage,
  Repeater,
  SaveActions,
  type SaveActionsStatus,
  StatusPill,
  useFormValidation,
  useLanguageHotkeys,
  useLanguageVersions,
} from "@/components/Profiles";
import { CompanyPicker } from "@/components/Profiles/ListFilters";
import { RegionsPicker } from "@/components/Profiles/RegionPicker";
import { RichTextField } from "@/components/Profiles/RichTextField";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesForm.module.css";
import { AuditPanel } from "../Audit/AuditPanel";

const KIND_LABELS: Record<FirmTabKind, string> = {
  client_testimonials: "Client testimonials",
  diversity: "Diversity & Inclusion",
};

// Static language options the user can add via the language tab bar.
// TODO(profiles): replace with the language taxonomy from profiles-api.
const LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
];

const RICH_TEXT_MAX = 50_000;

// Per-version (language-specific) text fields.
type FirmTabVersion = {
  title: string;
  content: string;
  introContent: string;
};

function emptyVersion(): FirmTabVersion {
  return {
    title: "",
    content: "",
    introContent: "",
  };
}

function versionFromTab(tab: FirmTab & Record<string, unknown>): LanguageVersion<FirmTabVersion> {
  const str = (k: string) => (typeof tab[k] === "string" ? (tab[k] as string) : "");
  return {
    languageCode: str("languageCode") || "en",
    title: tab.title ?? "",
    content: str("content"),
    introContent: str("introContent"),
  };
}

// Map a loaded server array of entry rows (which may carry extra id/tabId/
// sortOrder fields) down to the UI input shape, reading defensively.
function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}
const s = (row: Record<string, unknown>, k: string): string =>
  typeof row[k] === "string" ? (row[k] as string) : "";
const num = (row: Record<string, unknown>, k: string, fallback: number): number =>
  typeof row[k] === "number" ? (row[k] as number) : fallback;

// Client-side validation (mirror profiles-api Insert*EntrySchema, trimmed to
// the free-text sub-fields the server marks `.min(1)`).
const TestimonialEntrySchema = z.object({
  clientName: z.string().min(1, "Client name is required"),
  testimonial: z.string().min(1, "Testimonial is required"),
});

const FirmTabDraftSchema = z.object({
  title: z.string().min(1, "Title is required"),
  content: z.string().max(RICH_TEXT_MAX).optional(),
  introContent: z.string().max(RICH_TEXT_MAX).optional(),
  // Only supplied when editing a client testimonials tab; otherwise undefined
  // and Zod skips it.
  testimonialEntries: z.array(TestimonialEntrySchema).optional(),
});

type FirmTabFieldKey = "title";
const FIRM_TAB_FIELD_KEYS = ["title"] as const satisfies ReadonlyArray<FirmTabFieldKey>;
const FIRM_TAB_FIELD_LABELS: Record<FirmTabFieldKey, string> = {
  title: "Title",
};

// Local UI array keys (independent of the wire key `entries`).
const FIRM_TAB_ARRAY_KEYS = ["testimonialEntries"] as const;

const FIRM_TAB_ARRAY_FIELD_LABELS = {
  testimonialEntries: {
    section: "Testimonial entries",
    rowNoun: "testimonial",
    subFields: { clientName: "Client name", testimonial: "Testimonial" },
  },
} as const;

export type FirmTabFormMode = "edit" | "new";

export type FirmTabFormProps = {
  kind: FirmTabKind;
  mode: FirmTabFormMode;
  /** Required for edit; ignored for new. */
  id?: string;
  /** Required for new (until the user picks a company); ignored for edit. */
  initialCompanyId?: string | null;
};

export function FirmTabForm({ kind, mode, id, initialCompanyId }: FirmTabFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = mode === "new";
  const { hasPermission } = usePermissions();
  const canPublish = hasPermission("profiles.publish");
  const titleRef = useRef<HTMLInputElement | null>(null);

  const existingQuery = useQuery({
    queryKey: ["profiles", "firm-tabs", kind, id ?? "new"],
    queryFn: () => firmTabsService.getByKind(kind, id as string),
    enabled: !isNew && Boolean(id),
  });

  const [companyId, setCompanyId] = useState<string | null>(initialCompanyId ?? null);
  const [status, setStatus] = useState<ContentStatus>("draft");
  const [regions, setRegions] = useState<RegionAssignment[]>([]);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    errors: fieldErrors,
    arrayErrors,
    summary,
    orphanMessages,
    validate,
    clearError,
    clearArrayError,
    setServerIssues,
  } = useFormValidation({
    schema: FirmTabDraftSchema,
    labels: FIRM_TAB_FIELD_LABELS,
    knownKeys: FIRM_TAB_FIELD_KEYS,
    arrayKeys: FIRM_TAB_ARRAY_KEYS,
    arrayFieldLabels: FIRM_TAB_ARRAY_FIELD_LABELS,
  });

  // Child collections — shared across languages (NOT per version), exactly like
  // account-ui. Hydrated from the loaded tab below.
  const [testimonialEntries, setTestimonialEntries] = useState<FirmTabTestimonialEntryInput[]>([]);
  // BACKEND GAP: profiles-api diversity controller does NOT accept an images
  // array. We keep it in local state and DO NOT send it (see buildPayload).
  const [diversityImages, setDiversityImages] = useState<DiversityImage[]>([]);

  const initialVersions = useMemo<LanguageVersion<FirmTabVersion>[]>(() => {
    const existing = existingQuery.data as (FirmTab & Record<string, unknown>) | undefined;
    if (!existing) {
      return isNew
        ? [{ ...emptyVersion(), languageCode: "en" } as LanguageVersion<FirmTabVersion>]
        : [];
    }
    return [versionFromTab(existing)];
  }, [existingQuery.data, isNew]);

  const createEmpty = useCallback((_code: string): FirmTabVersion => emptyVersion(), []);

  const {
    versions,
    activeCode,
    setActiveCode,
    activeVersion,
    patchActive,
    addLanguage,
    removeLanguage,
    markSaved,
    tabOptions,
  } = useLanguageVersions<FirmTabVersion>({
    initial: initialVersions,
    createEmpty,
    defaultActiveCode: "en",
  });

  useLanguageHotkeys({ codes: tabOptions.map(o => o.code), activeCode, setActiveCode });

  // Seed scalar + child state when the existing tab loads.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const existing = existingQuery.data as (FirmTab & Record<string, unknown>) | undefined;
    if (!existing) return;
    const st = existing.status;
    setStatus(st === "draft" || st === "published" ? st : "draft");
    setCompanyId(existing.companyId ?? null);
    // Country-only (§4.2): ignore any legacy `kind:"region"` rows on hydration.
    setRegions(
      Array.isArray(existing.regions)
        ? (existing.regions as RegionAssignment[]).filter(r => r.kind === "country")
        : [],
    );
    setLastSavedAt(existing.updatedAt ?? existing.createdAt ?? null);

    // The client testimonials GET returns its child collection under
    // `entries`. Map down to UI shape.
    setTestimonialEntries(
      asArray(existing.entries).map((row, i) => ({
        clientName: s(row, "clientName"),
        clientCompany: s(row, "clientCompany"),
        testimonial: s(row, "testimonial"),
        sortOrder: num(row, "sortOrder", i + 1),
      })),
    );
  }, [existingQuery.data]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Resolve the firm's display NAME from the address-book so edit mode shows a
  // human-readable name instead of the raw company_id UUID.
  const companyQuery = useQuery({
    queryKey: ["address-book", "company", companyId],
    queryFn: () => companies.getById(companyId as string),
    enabled: !isNew && Boolean(companyId),
  });
  const firmName = companyQuery.data?.data?.name ?? companyId ?? "";

  const buildPayload = (version: FirmTabVersion, targetStatus: ContentStatus) => {
    const base: Record<string, unknown> = {
      title: version.title,
      languageCode: activeCode,
      status: targetStatus,
      regions,
    };
    if (isNew) base.companyId = companyId;

    switch (kind) {
      case "diversity":
        base.content = version.content;
        // BACKEND GAP: the diversity controller does not accept an images array,
        // so `diversityImages` is intentionally NOT sent.
        break;
      case "client_testimonials":
        base.introContent = version.introContent;
        base.entries = testimonialEntries;
        break;
    }
    return base;
  };

  const saveMutation = useMutation({
    mutationFn: async (targetStatus: ContentStatus): Promise<FirmTab> => {
      if (!activeVersion) throw new Error("No active language version");
      const payload = buildPayload(activeVersion, targetStatus);
      if (isNew) {
        if (!companyId) throw new Error("Company is required to create a firm tab");
        return firmTabsService.create(kind, payload);
      }
      return firmTabsService.update(kind, id as string, payload);
    },
    onSuccess: async (saved, targetStatus) => {
      await queryClient.invalidateQueries({ queryKey: ["profiles", "firm-tabs"] });
      const st = saved.status;
      setStatus(st === "draft" || st === "published" ? st : targetStatus);
      setLastSavedAt(saved.updatedAt ?? new Date().toISOString());
      markSaved(versions.map(v => ({ ...v, isDirty: false, isNew: false })));
      navigate(ROUTES.profiles.firmTabKindList(kind));
    },
    onError: (err: Error) => {
      const issues = extractZodIssues(err);
      if (issues && issues.length > 0) {
        setServerIssues(issues);
        setFormError(null);
        return;
      }
      setFormError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => firmTabsService.remove(kind, id as string),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["profiles", "firm-tabs"] });
      navigate(ROUTES.profiles.firmTabKindList(kind));
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const handleSave = (targetStatus: SaveActionsStatus) => {
    setFormError(null);
    setRegionsError(null);
    setCompanyError(null);

    if (isNew && !companyId) {
      setCompanyError("Company is required");
      return;
    }
    if (!activeVersion) return;

    const parsed = validate({
      title: activeVersion.title,
      content: activeVersion.content || undefined,
      introContent: activeVersion.introContent || undefined,
      // Only validate the array for the kind currently being edited.
      testimonialEntries: kind === "client_testimonials" ? testimonialEntries : undefined,
    });
    if (parsed === null) {
      if (!activeVersion.title.trim()) {
        setTimeout(() => titleRef.current?.focus(), 0);
      }
      return;
    }
    if (regions.length === 0) {
      setRegionsError("At least one region is required");
      return;
    }

    setStatus(targetStatus);
    saveMutation.mutate(targetStatus);
  };

  const availableLanguagesToAdd = useMemo(
    () =>
      LANGUAGE_OPTIONS.filter(opt => !versions.some(v => v.languageCode === opt.value)).map(
        opt => ({ code: opt.value, label: opt.label }),
      ),
    [versions],
  );

  const kindLabel = KIND_LABELS[kind];
  const pageTitle = isNew ? `New ${kindLabel} tab` : `Edit ${kindLabel} tab`;
  const version = activeVersion ?? ({ ...emptyVersion() } as LanguageVersion<FirmTabVersion>);

  if (!isNew && existingQuery.isPending) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={pageTitle} />
        <p role="status" className={styles.muted}>
          Loading…
        </p>
      </div>
    );
  }
  if (!isNew && existingQuery.isError) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={pageTitle} />
        <p role="alert" className={styles.errorBanner}>
          {existingQuery.error instanceof Error
            ? existingQuery.error.message
            : "Failed to load firm tab"}
        </p>
      </div>
    );
  }

  const renderKindSections = () => {
    switch (kind) {
      case "diversity":
        return (
          <>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Content</h2>
              <RichTextField
                label="Content"
                preset="basic_enhanced"
                mediaEnabled={false}
                value={version.content}
                onChange={html => patchActive({ content: html })}
              />
            </section>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Images</h2>
              {/* BACKEND GAP: not persisted by profiles-api yet — local-only. */}
              <Repeater<DiversityImage>
                items={diversityImages}
                onChange={setDiversityImages}
                createEmpty={() => ({ mediaHash: null, mediaFilename: null, caption: "" })}
                addLabel="Add image"
                emptyState="No images added yet."
                renderRow={(item, _index, update) => (
                  <>
                    <ImageUploadField
                      label="Image"
                      value={
                        item.mediaHash && item.mediaFilename
                          ? { hash: item.mediaHash, filename: item.mediaFilename }
                          : null
                      }
                      onChange={ref =>
                        update({
                          mediaHash: ref?.hash ?? null,
                          mediaFilename: ref?.filename ?? null,
                        })
                      }
                    />
                    <Input
                      label="Caption"
                      value={item.caption ?? ""}
                      onChange={e => update({ caption: e.target.value })}
                      fullWidth
                    />
                  </>
                )}
              />
            </section>
          </>
        );

      case "client_testimonials":
        return (
          <>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <RichTextField
                label="Intro content"
                preset="basic_enhanced"
                mediaEnabled={false}
                value={version.introContent}
                onChange={html => patchActive({ introContent: html })}
              />
            </section>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Testimonial entries</h2>
              <Repeater<FirmTabTestimonialEntryInput>
                items={testimonialEntries}
                onChange={setTestimonialEntries}
                createEmpty={() => ({
                  clientName: "",
                  clientCompany: "",
                  testimonial: "",
                  sortOrder: testimonialEntries.length + 1,
                })}
                addLabel="Add testimonial"
                emptyState="No testimonials added yet."
                rowErrors={arrayErrors.testimonialEntries}
                renderRow={(item, index, update, errs) => (
                  <>
                    <Input
                      label="Client name"
                      value={item.clientName}
                      onChange={e => {
                        update({ clientName: e.target.value });
                        clearArrayError("testimonialEntries", index, "clientName");
                      }}
                      error={errs?.clientName}
                      required
                      fullWidth
                    />
                    <Input
                      label="Client company"
                      value={item.clientCompany ?? ""}
                      onChange={e => update({ clientCompany: e.target.value })}
                      fullWidth
                    />
                    <RichTextField
                      label="Testimonial"
                      preset="basic"
                      mediaEnabled={false}
                      value={item.testimonial}
                      onChange={html => {
                        update({ testimonial: html });
                        clearArrayError("testimonialEntries", index, "testimonial");
                      }}
                      error={errs?.testimonial}
                    />
                  </>
                )}
              />
            </section>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={pageTitle}
        subtitle={
          !isNew && lastSavedAt
            ? `Last updated ${new Date(lastSavedAt).toLocaleString()}`
            : undefined
        }
      >
        <div className={styles.headerActions}>
          <Button
            variant="outlined"
            onClick={() => navigate(ROUTES.profiles.firmTabKindList(kind))}
          >
            Cancel
          </Button>
          <SaveActions
            layout="inline"
            currentStatus={status}
            canPublish={canPublish}
            isSaving={saveMutation.isPending}
            onSave={handleSave}
            onDelete={!isNew ? () => setDeleteOpen(true) : undefined}
          />
        </div>
      </PageHeader>

      <LanguageTabBar
        languages={tabOptions}
        activeCode={activeCode}
        onChange={setActiveCode}
        availableLanguages={availableLanguagesToAdd}
        onAdd={addLanguage}
        onRemove={versions.length > 1 ? removeLanguage : undefined}
      />

      <FormValidationSummary
        entries={summary}
        onEntryActivate={key => {
          if (key === "title") titleRef.current?.focus();
        }}
      />

      {(formError || orphanMessages.length > 0) && (
        <div role="alert" className={styles.errorBanner}>
          {formError ?? orphanMessages.join("\n")}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.main}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Basics</h2>
            <div className={styles.fieldGridSingle}>
              <Input
                ref={titleRef}
                label="Title"
                value={version.title}
                onChange={e => {
                  patchActive({ title: e.target.value });
                  clearError("title");
                }}
                required
                aria-required="true"
                error={fieldErrors.title}
                fullWidth
              />
              <div className={styles.fieldGrid}>
                <Input label="Language" value={labelForLanguage(activeCode)} disabled fullWidth />
                <Input label="Tab kind" value={kindLabel} disabled fullWidth />
              </div>
            </div>
          </section>

          {renderKindSections()}
        </div>

        <aside className={styles.sidebar} aria-label="Sidebar">
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Status</h2>
            <StatusPill status={status} />
            <p className={styles.labelHint}>
              {lastSavedAt
                ? `Last saved ${new Date(lastSavedAt).toLocaleString()}`
                : "Not saved yet"}
            </p>
          </section>

          {isNew && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Company</h2>
              <CompanyPicker
                value={companyId}
                onChange={next => {
                  setCompanyId(next);
                  if (next) setCompanyError(null);
                }}
              />
              {companyError && (
                <p role="alert" className={styles.fieldError}>
                  {companyError}
                </p>
              )}
              <p className={styles.labelHint}>Required: select the firm this tab belongs to.</p>
            </section>
          )}

          {!isNew && companyId && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Company</h2>
              <p className={styles.muted}>{firmName}</p>
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Countries</h2>
            <RegionsPicker
              label="Countries (at least one required)"
              value={regions}
              onChange={next => {
                setRegions(next);
                if (next.length > 0) setRegionsError(null);
              }}
              error={regionsError}
            />
          </section>

          {!isNew && id && <AuditPanel entityType="firm_tab" entityId={id} />}
        </aside>
      </div>

      {deleteOpen && !isNew && (
        <ConfirmationModal
          title={`Delete ${kindLabel} tab?`}
          message={`Delete ${kindLabel.toLowerCase()} tab "${version.title || "(untitled)"}"?`}
          warningMessage="This cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setDeleteOpen(false)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync();
          }}
        />
      )}
    </div>
  );
}

export default FirmTabForm;
